import { supabase } from '../../../core/lib/supabase';
import { ProjectVariable } from '../../../types/navigationTypes';

/* global Word */

/**
 * Obtiene las project variables de una ejecución
 */
export async function getProjectVariablesByExecution(
  executionId: string
): Promise<ProjectVariable[]> {
  try {
    console.log('📊 ========================================');
    console.log('📊 Fetching project variables...');
    console.log('📊 ========================================');
    console.log('📊 Execution ID:', executionId);

    const { data: variables, error } = await supabase
      .from('project_variables')
      .select('*')
      .eq('execution_id', executionId)
      .order('created_at', { ascending: false });

    console.log('📊 Query result:', { count: variables?.length, error });

    if (error) {
      console.error('❌ Error fetching project variables:', error);
      throw error;
    }

    if (!variables || variables.length === 0) {
      console.log('⚠️ No project variables found for execution:', executionId);
      return [];
    }

    console.log(`📊 Found ${variables.length} project variables`);
    return variables as ProjectVariable[];
  } catch (error) {
    console.error('❌ Error in getProjectVariablesByExecution:', error);
    throw error;
  }
}

/**
 * Crea o actualiza una project variable
 */
export async function upsertProjectVariable(
  executionId: string,
  name: string,
  value: string | null
): Promise<ProjectVariable> {
  try {
    console.log('📝 Upserting project variable:', { executionId, name, value });

    const { data, error } = await supabase
      .from('project_variables')
      .upsert({
        execution_id: executionId,
        name,
        value,
        updated_at: new Date().toISOString()
      }, {
        onConflict: 'execution_id,name'
      })
      .select()
      .single();

    if (error) {
      console.error('❌ Error upserting project variable:', error);
      throw error;
    }

    console.log('✅ Project variable upserted successfully');
    return data as ProjectVariable;
  } catch (error) {
    console.error('❌ Error in upsertProjectVariable:', error);
    throw error;
  }
}

/**
 * Elimina una project variable
 */
export async function deleteProjectVariable(variableId: string): Promise<void> {
  try {
    console.log('🗑️ Deleting project variable:', variableId);

    const { error } = await supabase
      .from('project_variables')
      .delete()
      .eq('id', variableId);

    if (error) {
      console.error('❌ Error deleting project variable:', error);
      throw error;
    }

    console.log('✅ Project variable deleted successfully');
  } catch (error) {
    console.error('❌ Error in deleteProjectVariable:', error);
    throw error;
  }
}

/**
 * Busca variables en el texto del documento (patrón {{variable}})
 */
export function findVariablesInText(text: string): string[] {
  // Expresión regular para encontrar variables entre {{}}
  // Permite letras, números, guiones bajos y espacios
  const regex = /\{\{([^}]+)\}\}/g;
  const matches = new Set<string>();
  let match;

  while ((match = regex.exec(text)) !== null) {
    const variableName = match[1].trim();
    // Solo agregar si no está vacío y no es solo espacios
    if (variableName.length > 0) {
      matches.add(variableName);
    }
  }

  return Array.from(matches);
}

/**
 * Busca variables en el documento actual y las guarda en Supabase
 * Solo guarda las variables que no existen ya en la ejecución
 */
export async function scanAndSaveDocumentVariables(
  executionId: string,
  documentText: string
): Promise<{ found: number; saved: number }> {
  try {
    console.log('🔍 Escaneando documento en busca de variables...');
    
    // Buscar variables en el texto
    const foundVariables = findVariablesInText(documentText);
    console.log(`📊 Variables encontradas en el documento: ${foundVariables.length}`, foundVariables);

    if (foundVariables.length === 0) {
      return { found: 0, saved: 0 };
    }

    // Obtener variables existentes
    const existingVariables = await getProjectVariablesByExecution(executionId);
    const existingNames = new Set(existingVariables.map(v => v.name.toLowerCase()));

    // Filtrar solo las variables nuevas (que no existen)
    const newVariables = foundVariables.filter(
      name => !existingNames.has(name.toLowerCase())
    );

    console.log(`💾 Variables nuevas a guardar: ${newVariables.length}`, newVariables);

    // Guardar las nuevas variables (sin valor inicial)
    let savedCount = 0;
    for (const variableName of newVariables) {
      try {
        await upsertProjectVariable(executionId, variableName, null);
        savedCount++;
      } catch (error) {
        console.error(`❌ Error guardando variable ${variableName}:`, error);
      }
    }

    console.log(`✅ Variables guardadas: ${savedCount} de ${foundVariables.length}`);
    return { found: foundVariables.length, saved: savedCount };
  } catch (error) {
    console.error('❌ Error escaneando variables del documento:', error);
    throw error;
  }
}

/**
 * Reemplaza todas las ocurrencias de una variable en el documento Word
 * @param variableName Nombre de la variable (sin {{}})
 * @param value Valor con el que reemplazar
 * @returns Número de ocurrencias reemplazadas
 */
export async function replaceVariableInDocument(
  variableName: string,
  value: string
): Promise<number> {
  if (!value || value.trim() === '') {
    return 0;
  }

  try {
    return await Word.run(async (context) => {
      const searchText = `{{${variableName}}}`;
      const searchResults = context.document.body.search(searchText, {
        matchCase: false,
        matchWholeWord: false // No usar matchWholeWord porque {{}} puede estar en medio de texto
      });

      searchResults.load('items');
      await context.sync();

      let replacedCount = 0;
      
      // Reemplazar en orden inverso para evitar problemas con índices
      for (let i = searchResults.items.length - 1; i >= 0; i--) {
        const range = searchResults.items[i];
        range.insertText(value, Word.InsertLocation.replace);
        replacedCount++;
      }

      await context.sync();
      
      console.log(`✅ Reemplazadas ${replacedCount} ocurrencias de {{${variableName}}}`);
      return replacedCount;
    });
  } catch (error) {
    console.error(`❌ Error reemplazando variable ${variableName}:`, error);
    throw error;
  }
}

/**
 * Reemplaza todas las variables que tengan valor en el documento
 * @param variables Lista de variables con sus valores
 * @returns Resumen de reemplazos realizados
 */
export async function replaceAllVariablesInDocument(
  variables: ProjectVariable[]
): Promise<{ 
  totalVariables: number; 
  replacedVariables: number; 
  totalOccurrences: number;
  details: Array<{ name: string; occurrences: number }>;
}> {
  const variablesWithValue = variables.filter(v => v.value && v.value.trim() !== '');
  
  if (variablesWithValue.length === 0) {
    return {
      totalVariables: variables.length,
      replacedVariables: 0,
      totalOccurrences: 0,
      details: []
    };
  }

  const details: Array<{ name: string; occurrences: number }> = [];
  let totalOccurrences = 0;

  try {
    // Reemplazar todas las variables en una sola operación Word.run para mejor rendimiento
    await Word.run(async (context) => {
      for (const variable of variablesWithValue) {
        const searchText = `{{${variable.name}}}`;
        const searchResults = context.document.body.search(searchText, {
          matchCase: false,
          matchWholeWord: false
        });

        searchResults.load('items');
        await context.sync();

        let occurrences = 0;
        
        // Reemplazar en orden inverso para evitar problemas con índices
        for (let i = searchResults.items.length - 1; i >= 0; i--) {
          const range = searchResults.items[i];
          range.insertText(variable.value!, Word.InsertLocation.replace);
          occurrences++;
        }

        if (occurrences > 0) {
          details.push({ name: variable.name, occurrences });
          totalOccurrences += occurrences;
        }
      }

      await context.sync();
    });

    console.log(`✅ Reemplazo completo: ${totalOccurrences} ocurrencias de ${details.length} variables`);
    
    return {
      totalVariables: variables.length,
      replacedVariables: details.length,
      totalOccurrences,
      details
    };
  } catch (error) {
    console.error('❌ Error reemplazando todas las variables:', error);
    throw error;
  }
}

